import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { XIcon, CoinsIcon, PiggyBankIcon, ArrowUpIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface Block {
  width: number;
  x: number;
  y: number;
  value: number;
}

interface BudgetCategory {
  name: string;
  allocation: number;
}

export const SavingsStackGame = () => {
  const { toast } = useToast();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [currentBlock, setCurrentBlock] = useState<Block | null>(null);
  const [direction, setDirection] = useState(1);
  const [speed, setSpeed] = useState(3);
  const [level, setLevel] = useState(1);
  const [viewportOffset, setViewportOffset] = useState(0);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [budgetCategories, setBudgetCategories] = useState<BudgetCategory[]>([
    { name: "Savings", allocation: 0 },
    { name: "Essentials", allocation: 0 },
    { name: "Entertainment", allocation: 0 },
    { name: "Education", allocation: 0 }
  ]);

  const BLOCK_HEIGHT = 40;
  const INITIAL_BLOCK_WIDTH = 200;
  const CANVAS_WIDTH = 600;
  const CANVAS_HEIGHT = 600;
  const GROUND_OFFSET = 100;
  const BASE_Y = CANVAS_HEIGHT - GROUND_OFFSET;

  useEffect(() => {
    if (!gameStarted || gameOver) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationFrameId: number;

    const gameLoop = () => {
      if (!currentBlock) {
        const newBlock: Block = {
          width: blocks.length === 0 ? INITIAL_BLOCK_WIDTH : blocks[blocks.length - 1].width,
          x: 0,
          y: BASE_Y - (blocks.length * BLOCK_HEIGHT),
          value: Math.pow(2, blocks.length + 1)
        };
        setCurrentBlock(newBlock);
      } else {
        setCurrentBlock(prev => {
          if (!prev) return null;
          let newX = prev.x + (speed * direction);
          
          if (newX <= 0 || newX + prev.width >= CANVAS_WIDTH) {
            setDirection(d => d * -1);
            newX = Math.max(0, Math.min(CANVAS_WIDTH - prev.width, newX));
          }
          
          return { ...prev, x: newX };
        });

        if (blocks.length > 4) {
          const stackHeight = blocks.length * BLOCK_HEIGHT;
          const targetOffset = Math.max(0, stackHeight - CANVAS_HEIGHT / 2);
          setViewportOffset(prev => {
            const diff = targetOffset - prev;
            return prev + (diff * 0.1);
          });
        }
      }

      drawGame(ctx);
      animationFrameId = requestAnimationFrame(gameLoop);
    };

    const drawGame = (ctx: CanvasRenderingContext2D) => {
      ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      
      // Draw ground with gradient
      const groundGradient = ctx.createLinearGradient(0, CANVAS_HEIGHT - GROUND_OFFSET, 0, CANVAS_HEIGHT);
      groundGradient.addColorStop(0, '#f0f0f0');
      groundGradient.addColorStop(1, '#e0e0e0');
      ctx.fillStyle = groundGradient;
      ctx.fillRect(0, CANVAS_HEIGHT - GROUND_OFFSET, CANVAS_WIDTH, GROUND_OFFSET);

      // Draw blocks with enhanced styling
      blocks.forEach((block) => {
        const adjustedY = block.y + viewportOffset;
        
        // Create gradient for block - removed transparency
        const blockGradient = ctx.createLinearGradient(
          block.x,
          adjustedY,
          block.x + block.width,
          adjustedY + BLOCK_HEIGHT
        );
        const hue = (blocks.indexOf(block) * 30) % 360;
        blockGradient.addColorStop(0, `hsl(${hue}, 70%, 65%)`);
        blockGradient.addColorStop(1, `hsl(${hue}, 70%, 55%)`);
        
        // Draw block shadow
        ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
        ctx.fillRect(
          block.x + 2,
          adjustedY + 2,
          block.width,
          BLOCK_HEIGHT
        );
        
        // Draw block with gradient
        ctx.fillStyle = blockGradient;
        ctx.beginPath();
        ctx.roundRect(block.x, adjustedY, block.width, BLOCK_HEIGHT, 4);
        ctx.fill();
        
        // Add subtle highlight
        const highlightGradient = ctx.createLinearGradient(
          block.x,
          adjustedY,
          block.x,
          adjustedY + 10
        );
        highlightGradient.addColorStop(0, 'rgba(255, 255, 255, 0.2)');
        highlightGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
        ctx.fillStyle = highlightGradient;
        ctx.fillRect(block.x, adjustedY, block.width, 10);
        
        // Draw value text with shadow
        ctx.save();
        ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
        ctx.font = "bold 16px Inter";
        ctx.textAlign = "center";
        ctx.fillText(
          `$${block.value}`,
          block.x + block.width / 2 + 1,
          adjustedY + BLOCK_HEIGHT / 2 + 7
        );
        ctx.fillStyle = "white";
        ctx.fillText(
          `$${block.value}`,
          block.x + block.width / 2,
          adjustedY + BLOCK_HEIGHT / 2 + 6
        );
        ctx.restore();
      });

      // Draw current block with same styling
      if (currentBlock) {
        const adjustedY = currentBlock.y + viewportOffset;
        
        // Create gradient for current block - solid colors
        const blockGradient = ctx.createLinearGradient(
          currentBlock.x,
          adjustedY,
          currentBlock.x + currentBlock.width,
          adjustedY + BLOCK_HEIGHT
        );
        const hue = (blocks.length * 30) % 360;
        blockGradient.addColorStop(0, `hsl(${hue}, 70%, 65%)`);
        blockGradient.addColorStop(1, `hsl(${hue}, 70%, 55%)`);
        
        // Draw block shadow
        ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
        ctx.fillRect(
          currentBlock.x + 2,
          adjustedY + 2,
          currentBlock.width,
          BLOCK_HEIGHT
        );
        
        // Draw current block with gradient
        ctx.fillStyle = blockGradient;
        ctx.beginPath();
        ctx.roundRect(currentBlock.x, adjustedY, currentBlock.width, BLOCK_HEIGHT, 4);
        ctx.fill();
        
        // Add subtle highlight
        const highlightGradient = ctx.createLinearGradient(
          currentBlock.x,
          adjustedY,
          currentBlock.x,
          adjustedY + 10
        );
        highlightGradient.addColorStop(0, 'rgba(255, 255, 255, 0.2)');
        highlightGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
        ctx.fillStyle = highlightGradient;
        ctx.fillRect(currentBlock.x, adjustedY, currentBlock.width, 10);
        
        // Draw value text with shadow
        ctx.save();
        ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
        ctx.font = "bold 16px Inter";
        ctx.textAlign = "center";
        ctx.fillText(
          `$${currentBlock.value}`,
          currentBlock.x + currentBlock.width / 2 + 1,
          adjustedY + BLOCK_HEIGHT / 2 + 7
        );
        ctx.fillStyle = "white";
        ctx.fillText(
          `$${currentBlock.value}`,
          currentBlock.x + currentBlock.width / 2,
          adjustedY + BLOCK_HEIGHT / 2 + 6
        );
        ctx.restore();
      }

      // Enhanced stats display
      // Background for stats
      ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
      ctx.roundRect(5, 5, 200, 80, 10);
      ctx.fill();
      
      // Savings display
      ctx.fillStyle = "#8B5CF6"; // Vivid purple
      ctx.font = "bold 24px Inter";
      ctx.textAlign = "left";
      ctx.fillText("Total Savings:", 15, 35);
      
      // Savings amount with gradient
      const savingsGradient = ctx.createLinearGradient(15, 45, 190, 45);
      savingsGradient.addColorStop(0, "#D946EF"); // Magenta pink
      savingsGradient.addColorStop(1, "#8B5CF6"); // Vivid purple
      ctx.fillStyle = savingsGradient;
      ctx.font = "bold 28px Inter";
      ctx.fillText(`$${score}`, 15, 65);
      
      // Level display with background
      ctx.fillStyle = "#1EAEDB"; // Bright blue
      ctx.font = "bold 20px Inter";
      ctx.textAlign = "right";
      
      // Level badge background
      ctx.beginPath();
      ctx.fillStyle = "#E5DEFF"; // Soft purple
      ctx.roundRect(CANVAS_WIDTH - 110, 10, 100, 40, 20);
      ctx.fill();
      
      // Level text
      ctx.fillStyle = "#6E59A5"; // Tertiary purple
      ctx.textAlign = "center";
      ctx.font = "bold 18px Inter";
      ctx.fillText(`Level ${level}`, CANVAS_WIDTH - 60, 35);
    };

    animationFrameId = requestAnimationFrame(gameLoop);

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [gameStarted, gameOver, currentBlock, blocks, direction, speed, level, viewportOffset]);

  const handleClick = () => {
    if (!currentBlock || !blocks.length && blocks.length === 0) {
      if (currentBlock) {
        setBlocks([currentBlock]);
        setScore(currentBlock.value);
      }
      setCurrentBlock(null);
      return;
    }

    const previousBlock = blocks[blocks.length - 1];
    const overlap = calculateOverlap(previousBlock, currentBlock);

    if (overlap <= 0) {
      handleGameOver();
    } else {
      const newBlock: Block = {
        width: overlap,
        x: Math.max(currentBlock.x, previousBlock.x),
        y: currentBlock.y,
        value: currentBlock.value
      };

      setBlocks(prev => [...prev, newBlock]);
      setScore(prev => prev + newBlock.value);
      setCurrentBlock(null);

      if (blocks.length % 5 === 0) {
        setSpeed(prev => prev + 0.5);
        setLevel(prev => prev + 1);
        toast({
          title: "Level Up!",
          description: "Speed increased! Stack carefully!",
          duration: 2000
        });
      }
    }
  };

  const calculateOverlap = (block1: Block, block2: Block) => {
    const left = Math.max(block1.x, block2.x);
    const right = Math.min(block1.x + block1.width, block2.x + block2.width);
    return right - left;
  };

  const handleGameOver = () => {
    setGameOver(true);
    setGameStarted(false);
    setShowBudgetModal(true);
    if (score > highScore) {
      setHighScore(score);
    }
    toast({
      title: "Game Over!",
      description: "Complete the budget exercise for another chance!",
    });
  };

  const startGame = () => {
    setGameStarted(true);
    setGameOver(false);
    setScore(0);
    setBlocks([]);
    setCurrentBlock(null);
    setSpeed(3);
    setLevel(1);
    setViewportOffset(0);
    setShowBudgetModal(false);
    setBudgetCategories([
      { name: "Savings", allocation: 0 },
      { name: "Essentials", allocation: 0 },
      { name: "Entertainment", allocation: 0 },
      { name: "Education", allocation: 0 }
    ]);
  };

  const handleBudgetSubmit = () => {
    const total = budgetCategories.reduce((sum, cat) => sum + cat.allocation, 0);
    
    if (total !== score) {
      toast({
        title: "Error",
        description: `Your total budget must equal your score (${score})`,
        variant: "destructive"
      });
      return;
    }

    const savingsCategory = budgetCategories.find(cat => cat.name === "Savings");
    const savingsPercentage = (savingsCategory?.allocation || 0) / score * 100;

    if (savingsPercentage < 20) {
      toast({
        title: "Try Again!",
        description: "You should save at least 20% of your earnings for a second chance!",
        variant: "destructive"
      });
      return;
    }

    setShowBudgetModal(false);
    setGameOver(false);
    setGameStarted(true);
    setBlocks([]);
    setCurrentBlock(null);
    setViewportOffset(0);
    
    toast({
      title: "Great budgeting!",
      description: "You've earned another life! Keep stacking!",
    });
  };

  const handleBudgetChange = (categoryName: string, value: string) => {
    const numValue = value === "" ? 0 : parseInt(value);
    setBudgetCategories(prev => 
      prev.map(cat => 
        cat.name === categoryName 
          ? { ...cat, allocation: numValue } 
          : cat
      )
    );
  };

  return (
    <div className="relative w-full max-w-2xl mx-auto">
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-2 right-2 z-10"
        onClick={() => setGameStarted(false)}
      >
        <XIcon className="h-4 w-4" />
      </Button>
      <canvas
        ref={canvasRef}
        width={CANVAS_WIDTH}
        height={CANVAS_HEIGHT}
        onClick={handleClick}
        className="border border-gray-200 rounded-lg shadow-lg cursor-pointer"
      />

      {/* Budget Exercise Modal */}
      <Dialog open={showBudgetModal} onOpenChange={setShowBudgetModal}>
        <DialogContent className="sm:max-w-[425px] bg-gradient-to-br from-green-50 to-emerald-50">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl">
              <PiggyBankIcon className="w-8 h-8 text-primary animate-bounce-small" />
              Want Another Chance?
            </DialogTitle>
            <DialogDescription className="space-y-4 pt-4">
              <div className="p-4 bg-white/50 rounded-lg text-center">
                <CoinsIcon className="w-12 h-12 mx-auto text-yellow-500 animate-spin-slow mb-2" />
                <p className="text-lg font-semibold">You've earned {score} coins!</p>
                <p className="mt-2">Budget them wisely for another life!</p>
                <p className="mt-2 text-sm text-muted-foreground">
                  Tip: Save at least 20% to continue playing!
                </p>
              </div>

              <div className="space-y-4">
                {budgetCategories.map((category) => (
                  <div key={category.name} className="space-y-2">
                    <Label htmlFor={category.name}>{category.name}</Label>
                    <Input
                      id={category.name}
                      type="number"
                      min="0"
                      max={score}
                      value={category.allocation}
                      onChange={(e) => handleBudgetChange(category.name, e.target.value)}
                      className="w-full"
                    />
                  </div>
                ))}

                <div className="flex justify-between items-center pt-2 text-sm font-medium">
                  <span>Total:</span>
                  <span className={`${
                    budgetCategories.reduce((sum, cat) => sum + cat.allocation, 0) === score 
                      ? 'text-green-500' 
                      : 'text-red-500'
                  }`}>
                    {budgetCategories.reduce((sum, cat) => sum + cat.allocation, 0)} / {score}
                  </span>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={startGame}>
              Start New Game
            </Button>
            <Button onClick={handleBudgetSubmit} className="bg-gradient-to-r from-primary to-green-600">
              Continue Playing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Game Over / Start Screen */}
      {!gameStarted && !showBudgetModal && (
        <Card className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-center">
              {gameOver ? "Game Over!" : "Savings Stack"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {gameOver && (
              <div className="text-center space-y-2">
                <p className="text-xl">Final Score: ${score}</p>
                <p className="text-lg">High Score: ${highScore}</p>
              </div>
            )}
            {!gameStarted && !gameOver && (
              <div className="space-y-4 text-center">
                <p>Stack blocks to build your savings!</p>
                <p>Click to place each block. The more precise your stack, the higher your savings will grow!</p>
              </div>
            )}
            <div className="flex justify-center">
              <Button onClick={startGame} size="lg">
                {gameOver ? "Play Again" : "Start Game"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
